import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import DebouncedTextInput from '../../../../components/slides/highlighted/common/debounced/DebouncedTextInput';
import ElementsToClickBeforeDisplay from '../../../../components/slides/highlighted/common/elementsToClickBeforeAfter/elementsToClickBeforeDisplay';
import SlideAsContent from '../../../../components/slides/highlighted/common/slideAsContent';
import Label from '../../../../incase-domain/components/label/Label';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {IElement} from '../../../../incase-domain/elements';
import {IElementModal} from '../../../../incase-domain/elements/modal';
import {ISlideOption} from '../../../../incase-domain/slide';
import {ELEMENT_NAME_MAX} from '../../../../incase-domain/validators/elements/constant';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import {getElementClickBeforeDisplayOptions} from '../../../../utils/element';

interface CreateModalElementModalProps {
    isOpen?: boolean;
    onClose: () => void;
    onConfirm: (modalData: {title: string; selectedSlideId: string; elementToClickForDisplay: string[]}) => void;
}

function CreateModalElementModal(props: CreateModalElementModalProps): JSX.Element {
    const {isOpen, onClose, onConfirm} = props;
    const {t} = useTranslation();

    const [title, setTitle] = useState<string>(t('designSidepanel.newTitle'));
    const [selectedSlideId, setSelectedSlideId] = useState<string>('');
    const [elementToClickForDisplay, setElementToClickForDisplay] = useState<string[]>([]);

    const currentSlide = useSelector(selectedSlideSelector);

    const slideElementOptions: ISlideOption[] = currentSlide.elements.reduce((reducedElements: ISlideOption[], slideElement: IElement) => {
        return [...reducedElements, ...getElementClickBeforeDisplayOptions(slideElement)];
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setTitle(t('designSidepanel.newTitle'));
            setSelectedSlideId('');
            setElementToClickForDisplay([]);
        }
    }, [isOpen, t]);

    const isFormValid = selectedSlideId && elementToClickForDisplay.length > 0;

    return (
        <Modal
            isOpen={!!isOpen}
            icon="solar:notification-unread-lines-bold"
            header={t('elementModal.modal.header', 'Add modal element')}
            description={t('elementModal.modal.description', 'Modal will open by clicking on chosen element')}
            onCancel={onClose}
            onConfirm={() => onConfirm({title, selectedSlideId, elementToClickForDisplay})}
            confirmDisabled={!isFormValid}
            showCancel
            showConfirm
            closeOnClickOutside={false}
            showOverflow
        >
            <div>
                <div>
                    <Label>{t('designSidepanel.title')}</Label>
                    <DebouncedTextInput
                        className="c-sidepanel__input"
                        onChange={(value) => setTitle(value)}
                        value={title}
                        maxLength={ELEMENT_NAME_MAX}
                    />
                </div>

                <div className="u-mb-2">
                    <SlideAsContent
                        fixed={false}
                        selectedSlideId={selectedSlideId}
                        highlightedElementChangeHandler={{
                            single: (field: keyof IElementModal, value: unknown) => {
                                if (field === 'selectedSlideId') {
                                    setSelectedSlideId(value as string);
                                }
                            },
                        }}
                    />
                </div>

                <div className="u-mb-2">
                    <ElementsToClickBeforeDisplay
                        slideElementOptions={slideElementOptions}
                        elementToClickForDisplay={elementToClickForDisplay}
                        highlightedElementChangeHandler={{
                            single: (field: keyof IElement, value: unknown) => {
                                if (field === 'elementToClickForDisplay') {
                                    setElementToClickForDisplay(value as string[]);
                                }
                            },
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
}

export default CreateModalElementModal;
